import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { NgbToast } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { ToastService } from '@patient-ui/shared-ui/utils';

/** The toast fields the renderer reads (ToastService stores them untyped). */
interface ScopedToast {
  /** Which renderer shows the toast; absent = the app-level renderer. */
  scope?: string;
}

/**
 * Renders the shared root `ToastService` queue.
 *
 * `scope` picks which toasts this instance shows: a toast raised with
 * `{ scope: 'orbi' }` renders only in a `scope="orbi"` container, and a toast
 * with no scope only in a container with no scope. Without it, every mounted
 * renderer would draw every toast (two copies once both the app-level and the
 * Orbi panel renderers are on the page).
 *
 * `toastTemplate` replaces the default toast body for callers with their own
 * design (the Orbi panel); its context is the toast. When set, the default body
 * styles are dropped so the caller's styles own the look.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'patient-ui-app-toasts',
  template: `
    <ngb-toast
      *ngFor="let toast of toasts"
      #toastRef="ngbToast"
      [ngClass]="toastClass(toast)"
      [autohide]="true"
      [delay]="toast.delay || 5000"
      (hidden)="toastService.remove(toast)"
    >
      <ng-container *ngIf="toastTemplate; else defaultToast">
        <ng-container
          *ngTemplateOutlet="toastTemplate; context: { $implicit: toast }"
        ></ng-container>
      </ng-container>
      <ng-template #defaultToast>
        <ng-template [ngIf]="isTemplate(toast)" [ngIfElse]="text">
          <ng-template [ngTemplateOutlet]="toast.textOrTpl"></ng-template>
        </ng-template>
        <ng-template #text>
          <i class="toast-icon material-icons-round text-white">{{
            toast.icon
          }}</i>
          {{ toast.textOrTpl }}
        </ng-template>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          (click)="toastRef.hide()"
        ></button>
      </ng-template>
    </ngb-toast>
  `,
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    '[class.ngb-toasts]': 'true',
    '[class.app-toasts--custom]': '!!toastTemplate',
  },
  styles: [
    `
      :host(:not(.app-toasts--custom)) ngb-toast + ngb-toast {
        margin-top: 0.75rem;
      }

      :host(:not(.app-toasts--custom)) ::ng-deep .toast-body {
        /* 16px inset + 24px close button + 16px gap from text */
        padding-right: 3.5rem;
      }

      .btn-close {
        position: absolute;
        top: 0.75rem;
        right: 1rem;
        margin: 0;
      }

      :host ::ng-deep .toast-singleline .btn-close {
        top: 50%;
        transform: translateY(-50%);
      }
    `,
  ],
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class ToastsContainer implements OnInit, AfterViewInit, OnDestroy {
  /** Shows only toasts raised with this `scope`; unset = unscoped toasts. */
  @Input() scope?: string;

  /** Custom toast body; its context (`$implicit`) is the toast. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() toastTemplate?: TemplateRef<any>;

  @ViewChildren(NgbToast, { read: ElementRef })
  private toastEls!: QueryList<ElementRef<HTMLElement>>;

  private singleLineToasts = new Set<unknown>();
  private resizeObserver?: ResizeObserver;
  private toastElsSub?: Subscription;
  private toastChangesSub?: Subscription;

  constructor(
    public toastService: ToastService,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  /** The toasts in this container's scope. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get toasts(): any[] {
    return (this.toastService.toasts as ScopedToast[]).filter(
      (toast) => toast.scope === this.scope
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isTemplate(toast: any) {
    return toast.textOrTpl instanceof TemplateRef;
  }

  toastClass(toast: { classname?: string }): string {
    return [
      toast.classname,
      this.singleLineToasts.has(toast) ? 'toast-singleline' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  ngOnInit(): void {
    // The queue is a plain array, so a container under an OnPush parent (the
    // Orbi panel) is never told a toast arrived. Mark it and its ancestors dirty.
    this.toastChangesSub = this.toastService.changes.subscribe(() =>
      this.changeDetector.markForCheck()
    );
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.ngZone.run(() => this.syncSingleLineToasts());
      });
    }
    this.observeCurrentToasts();
    this.toastElsSub = this.toastEls.changes.subscribe(() =>
      this.observeCurrentToasts()
    );
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.toastElsSub?.unsubscribe();
    this.toastChangesSub?.unsubscribe();
  }

  private observeCurrentToasts(): void {
    this.resizeObserver?.disconnect();
    this.toastEls.forEach((elRef) => {
      const body = elRef.nativeElement.querySelector('.toast-body');
      if (body) {
        this.resizeObserver?.observe(body);
      }
    });
    requestAnimationFrame(() => this.syncSingleLineToasts());
  }

  private syncSingleLineToasts(): void {
    const next = new Set<unknown>();
    const toasts = this.toasts;
    this.toastEls?.forEach((elRef, index) => {
      const toast = toasts[index];
      const body = elRef.nativeElement.querySelector('.toast-body');
      if (toast && body && body.clientHeight > 0 && body.clientHeight <= 60) {
        next.add(toast);
      }
    });

    if (!this.areSetsEqual(this.singleLineToasts, next)) {
      this.singleLineToasts = next;
      this.changeDetector.detectChanges();
    }
  }

  private areSetsEqual(left: Set<unknown>, right: Set<unknown>): boolean {
    if (left.size !== right.size) {
      return false;
    }
    for (const value of left) {
      if (!right.has(value)) {
        return false;
      }
    }
    return true;
  }
}
